// /Users/stuartmcgeer/portalworx/frontend/src/components/common/BreadCrumbs.tsx

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import axiosInstance from '../../services/axiosInstance';

// Container for the breadcrumb navigation
const BreadcrumbContainer = styled.nav`
  display: flex;
  justify-content: center;
  padding: 0rem;
`;

// Styling for the list of breadcrumbs
const BreadcrumbList = styled.ol`
  list-style: none;
  display: flex;
  gap: 0rem;
  color: #666; 
  font-size: 1rem;
  margin: 0rem;
  padding: 0rem;
`;

// Individual breadcrumb item styling
const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;

  a {
    text-decoration: none;
    color: #666;
    cursor: pointer;  // Ensures the cursor changes to a pointer when hovering
    &:hover {
      text-decoration: underline;
      color: #007bff;
    }
  }

  &.active {
    font-weight: bold;
    color: #007bff; 
  }

  &::after {
    content: ">";
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    color: #ccc; 
  }

  &:last-child::after {
    content: ""; 
  }
`;

// Props for BreadCrumbs component
interface BreadCrumbsProps {
  type: 'page' | 'folder';
  folderPath?: string[];
  onFolderClick?: (folderIndex: number) => void; // Add a handler for folder navigation
  className?: string;
}

const BreadCrumbs: React.FC<BreadCrumbsProps> = ({ type, folderPath, onFolderClick }) => {
  const location = useLocation();
  const [projectName, setProjectName] = useState<string | null>(null);

  useEffect(() => {
    if (type === 'page') {
      const pathParts = location.pathname.split('/').filter(Boolean);
      const projectId = pathParts.find(part => part.match(/^\d+$/));

      if (projectId) {
        const fetchProjectName = async () => {
          try {
            const response = await axiosInstance.get(`/projects/${projectId}`);
            setProjectName(response.data.name);
          } catch (error) {
            console.error('Error fetching project name:', error);
          }
        };
        fetchProjectName();
      } else {
        setProjectName(null);
      }
    }
  }, [location.pathname, type]);

  // Render breadcrumbs for page navigation
  if (type === 'page') {
    const pathParts = location.pathname.split('/').filter(Boolean);
    return (
      <BreadcrumbContainer>
        <BreadcrumbList>
          <BreadcrumbItem>
            <Link to="/dashboard">Home</Link>
          </BreadcrumbItem>
          {pathParts.map((part, index) => {
            const isLast = index === pathParts.length - 1;
            const path = `/${pathParts.slice(0, index + 1).join('/')}`;
            const breadcrumbLabel = part.match(/^\d+$/) && projectName ? projectName : part.charAt(0).toUpperCase() + part.slice(1);

            return (
              <BreadcrumbItem key={path} className={isLast ? 'active' : ''}>
                {isLast ? (
                  breadcrumbLabel
                ) : (
                  <Link to={path}>{breadcrumbLabel}</Link>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbList>
      </BreadcrumbContainer>
    );
  }

  // Render breadcrumbs for folder navigation
  if (type === 'folder' && folderPath) {
    return (
      <BreadcrumbContainer>
        <BreadcrumbList>
          {folderPath.map((folder, index) => {
            const isLast = index === folderPath.length - 1;

            return (
              <BreadcrumbItem key={index} className={isLast ? 'active' : ''}>
                {isLast ? (
                  folder
                ) : (
                  <a
                    onClick={() => onFolderClick && onFolderClick(index)}
                  >
                    {folder}
                  </a>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbList>
      </BreadcrumbContainer>
    );
  }

  return null;
};

export default BreadCrumbs;
